import { useId, useMemo } from 'react';
import {
  BarChart as RechartsBar,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import type { ColumnDef, BarMapping, VisualizationConfig } from '@deckspace/types';
import { getColorsForIntent, getThemeColors } from '../../theme/index.js';
import { createChartFormatter } from '../../utils/chart-formatter.js';
import { ChartTooltip } from '../../components/ChartTooltip.js';
import { prepCartesianSeries } from '../shared/cartesian-utils.js';

interface Props {
  rows: Record<string, unknown>[];
  columnDefs: ColumnDef[];
  mapping: BarMapping;
  config: VisualizationConfig;
  width?: number;
  height?: number;
}

export function BarChart({ rows, columnDefs, mapping, config, width, height }: Props) {
  const uid = useId();
  const series = useMemo(
    () =>
      rows.length === 0
        ? { labels: [], datasets: [] }
        : prepCartesianSeries(rows, columnDefs, mapping),
    [rows, columnDefs, mapping]
  );

  const datasets = useMemo(() => series.datasets ?? [], [series.datasets]);

  const chartData = useMemo(
    () =>
      series.labels.map((label, index) => {
        const point: Record<string, string | number> = { name: label };
        datasets.forEach((dataset) => {
          point[dataset.label] = dataset.values[index] ?? 0;
        });
        return point;
      }),
    [series.labels, datasets]
  );

  if (!series.labels.length || !datasets.length) {
    return <div className="text-muted-foreground">No data available</div>;
  }

  const datasetCount = datasets.length;
  const colors = getColorsForIntent(config.intent, datasetCount);
  const themeColors = getThemeColors();

  const formatValue = createChartFormatter(datasets);

  return (
    <RechartsBar
      width={width ?? 400}
      height={height ?? 300}
      data={chartData}
      margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
    >
      <defs>
        {colors.map((color, index) => (
          <linearGradient
            key={`bar-gradient-${index}-${uid}`}
            id={`bar-gradient-${index}-${uid}`}
            x1="0"
            y1="0"
            x2="1"
            y2="0"
          >
            <stop offset="0%" stopColor={color} stopOpacity={0.95} />
            <stop offset="100%" stopColor={color} stopOpacity={0.7} />
          </linearGradient>
        ))}
      </defs>
      <CartesianGrid strokeDasharray="3 3" stroke={themeColors.border} />
      <XAxis
        dataKey="name"
        tick={{ fontSize: 12, fill: themeColors.textSecondary }}
        stroke={themeColors.textMuted}
      />
      <YAxis
        width="auto"
        tick={{ fontSize: 12, fill: themeColors.textSecondary }}
        stroke={themeColors.textMuted}
        tickFormatter={(value) => formatValue(value, 0)}
      />
      <Tooltip content={<ChartTooltip formatter={formatValue} />} />
      <Legend wrapperStyle={{ color: themeColors.textPrimary }} />
      {datasets.map((dataset, index) => (
        <Bar
          key={dataset.label}
          dataKey={dataset.label}
          fill={`url(#bar-gradient-${index % colors.length}-${uid})`}
          stroke={colors[index % colors.length]}
          strokeWidth={2}
          radius={[4, 4, 0, 0]}
        />
      ))}
    </RechartsBar>
  );
}
